/** 키워드 제안 — candidates for the app's keywords, folded under the keyword
 *  table (S3).
 *
 *  Four sources, each a call out: `local` (app name and category), `competitors`
 *  (TF-IDF over the competitors' text), `llm`, and `related` (needs a seed
 *  word). A result is a row of chips per source; a chip adds the term as a
 *  tracked keyword and says so in a toast. Each run is bound to the country it
 *  was started in, so a chip lands in the storefront its suggestions were
 *  sourced for even if the filter above is switched later. */

import { useState } from 'react'
import { Check, Plus } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type App, type SuggestionResponse } from '@/api/client'
import { invalidateKeywordData } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { Button, Code, CountryTag, Disclosure, errorText, Input, useToast } from '@/ui'

export function SuggestPanel({ app, country, scoped }: { app: App; country: string; scoped: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [seed, setSeed] = useState('')
  const [data, setData] = useState<SuggestionResponse | null>(null)
  const [resultCountry, setResultCountry] = useState(country)
  const [added, setAdded] = useState<Set<string>>(new Set())

  const run = useMutation({
    mutationFn: (sources: string[]) => api.suggestKeywords(app.id, { sources, seed, country, limit: 20 }),
    onSuccess: (d) => {
      setData(d)
      setResultCountry(country)
      setAdded(new Set())
    },
    onError: (e) => toast.error(errorText(e)),
  })
  const addOne = useMutation({
    mutationFn: (t: string) =>
      api.createKeyword(app.id, { term: t, country: resultCountry, locale: app.primary_locale || 'en-US', source: 'suggestion', is_tracking: true }),
    onSuccess: (_, t) => {
      setAdded((prev) => new Set(prev).add(t))
      invalidateKeywordData(qc, app.id)
      toast.success(`“${t}”를 ${resultCountry.toUpperCase()} 키워드로 추가했다`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  return (
    <Disclosure title="키워드 제안" hint="앱 이름·경쟁사·LLM에서 후보를 받는다">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-muted-foreground">
        <span>{scoped ? '범위' : '기본 범위'}</span>
        <CountryTag code={country} />
        <span>제안은 외부 서비스를 부른다. 누른 것만 실행된다.</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={run.isPending} onClick={() => run.mutate(['local'])}>
          앱 이름·카테고리에서
        </Button>
        <Button disabled={run.isPending} onClick={() => run.mutate(['competitors'])}>
          경쟁사에서 (TF-IDF)
        </Button>
        <Button disabled={run.isPending} onClick={() => run.mutate(['llm'])}>
          LLM 제안
        </Button>
        <span className="inline-flex items-center gap-1.5">
          <Input aria-label="시드 키워드" className="w-40" placeholder="시드 키워드" value={seed} onChange={(e) => setSeed(e.target.value)} />
          <Button disabled={run.isPending || !seed.trim()} onClick={() => run.mutate(['related'])}>
            연관어
          </Button>
        </span>
      </div>

      {run.isPending && <p className="text-table text-muted-foreground" role="status">제안을 만드는 중</p>}
      {data && (
        <div className="space-y-3">
          {Object.entries(data.sources).map(([src, terms]) => (
            <section key={src} aria-label={`${src} 제안`} className="space-y-1.5">
              <h3 className="text-table font-medium">
                <Code>{src}</Code>
              </h3>
              {terms.length === 0 ? (
                <p className="text-meta text-muted-foreground">제안이 없다.</p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {terms.map((t) => {
                    const done = added.has(t)
                    return (
                      <li key={t}>
                        <button
                          type="button"
                          disabled={done || addOne.isPending}
                          onClick={() => addOne.mutate(t)}
                          title={done ? '추가했다' : '추적 키워드로 추가'}
                          className={cn(
                            'inline-flex h-7 items-center gap-1 rounded-full border border-border bg-background px-2.5 text-table transition-colors',
                            'hover:border-accent hover:bg-accent/10 disabled:pointer-events-none',
                            done && 'opacity-50',
                          )}
                        >
                          {done ? <Check size={12} aria-hidden /> : <Plus size={12} aria-hidden />}
                          {t}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>
          ))}
          {Object.entries(data.errors).map(([src, msg]) => (
            <p key={src} className="break-words font-mono text-meta text-status-failed">
              {src}: {msg}
            </p>
          ))}
        </div>
      )}
    </Disclosure>
  )
}
